import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import { BK_HIT, bkHit } from './layout'
import { photoRatioOf, slotCountOfCategory } from './cutCategories'
import { nextBkStep, paysFirst, takeBkPaymentSkip } from './flow'

/**
 * Screen 2 — "choose your cut": 2 strip or 3 strip, on one 6x8 sheet.
 *
 * The guest tears the sheet into three 2x8 strips of four photos. "2 strip" is
 * eight photos with a Bingkai Kenangan strip in the third column; "3 strip" is
 * twelve photos. The cut decides the price, so with the Studio's "Start with
 * payment" off (both BK projects) it comes BEFORE payment:
 * welcome → cut → payment → filter. See flow.ts.
 *
 * This rides on `checkout`, which is free here because BK takes payment on its
 * own `payment` screen. That leaves `frame` to mean the border and `edit` to
 * mean the sticker, which is what each of those screens actually is.
 *
 * The cut is a frame CATEGORY: `captureMode: 'frame-based'` counts a frame's
 * unique `photo-N` slots, so committing the 3-strip category makes capture count
 * to twelve on its own. The specific border is chosen after capture, on `frame`.
 */
const Checkout: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { frameCategories, config } = useConfigStore()

  /**
   * Order the two cuts by the shots they actually ask for, so the left plaque is
   * always the shorter session regardless of how the categories were named in
   * the dashboard.
   */
  const cuts = useMemo(
    () =>
      [...frameCategories]
        .map((category) => ({ category, slots: slotCountOfCategory(category) }))
        .filter((cut) => cut.slots > 0)
        .sort((a, b) => a.slots - b.slots),
    [frameCategories]
  )

  const chooseCut = (index: number): void => {
    const cut = cuts[index]
    if (!cut) return

    const frame = cut.category.frames?.[0]
    if (!frame) return

    setSessionState({
      userPreferences: { frameId: frame._id, framePhotoRatio: photoRatioOf(frame) }
    })
    let next = nextBkStep(config?.pages, 'checkout')
    // The operator skipped payment here, before a cut existed: step over it now.
    if (next === 'payment' && takeBkPaymentSkip()) {
      next = nextBkStep(config?.pages, 'payment')
      logActivity('BK payment skipped by operator (asked on the cut screen)', { to: next })
    }
    logActivity(`BK cut chosen: ${cut.category.name} (${cut.slots} shots)`, { to: next })
    uploadSessionState()
    navigate(`/${projectId}/${next}`)
  }
  const remaining = useBkPageTimeout(config?.pages?.checkout?.settings?.checkoutTimeout, () => {
    if (paysFirst(config?.pages)) {
      // The guest has paid: carry on with the shorter cut, as stock does.
      chooseCut(0)
      return
    }
    // Nobody chose and nobody has paid yet: end the session rather than open
    // a charge for a cut no one picked.
    logActivity('BK cut screen timed out; back to welcome')
    void sessionCleanup('checkout_timeout')
  })

  return (
    <BkScreen plate="cut.webp">
      {/* Plaque plus its strip illustration, as one target each. */}
      <button
        aria-label="2 strip"
        onClick={() => chooseCut(0)}
        disabled={!cuts[0]}
        style={bkHit(BK_HIT.checkout.two)}
        className="cursor-pointer bg-transparent disabled:cursor-not-allowed"
      />
      <button
        aria-label="3 strip"
        onClick={() => chooseCut(1)}
        disabled={!cuts[1]}
        style={bkHit(BK_HIT.checkout.three)}
        className="cursor-pointer bg-transparent disabled:cursor-not-allowed"
      />
      <BkTimer seconds={remaining} />
    </BkScreen>
  )
}

export default Checkout
